import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail } from "lucide-react";

interface NewsletterSignupProps {
  /** Visual variant: "inline" for footer/sidebar, "card" for standalone section */
  variant?: "inline" | "card";
}

export const NewsletterSignup = ({ variant = "inline" }: NewsletterSignupProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("newsletter-signup", {
        body: { email },
      });

      if (error) throw error;

      setSubscribed(true);
      setEmail("");
      toast({
        title: "You're subscribed!",
        description: "We'll keep you in the loop with knitting tips and updates.",
      });
    } catch (error) {
      console.error("Newsletter signup error:", error);
      toast({
        title: "Couldn't subscribe",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className={variant === "card" ? "text-center py-4" : ""}>
        <p className="text-yarn-juniper font-medium text-sm">
          Thanks for subscribing! Check your inbox soon.
        </p>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className="bg-yarn-quartz/50 border border-yarn-orchid/10 rounded-lg p-6 text-center">
        <Mail className="w-8 h-8 text-primary mx-auto mb-3" />
        <h3 className="font-playfair text-lg font-semibold text-primary mb-1">
          Stay in the Loop
        </h3>
        <p className="text-sm text-yarn-slate mb-4">
          Knitting tips, new features, and pattern inspiration — no spam, ever.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
          <Input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1"
            disabled={loading}
          />
          <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90 text-white">
            {loading ? "Subscribing..." : "Subscribe"}
          </Button>
        </form>
      </div>
    );
  }

  // Inline variant (for footer)
  return (
    <div>
      <h4 className="font-playfair font-semibold text-primary mb-2 text-sm">
        Stay in the Loop
      </h4>
      <p className="text-xs text-yarn-slate mb-3">
        Tips, updates, and pattern inspiration.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="text-sm h-9"
          disabled={loading}
        />
        <Button
          type="submit"
          size="sm"
          disabled={loading}
          className="bg-primary hover:bg-primary/90 whitespace-nowrap"
        >
          {loading ? "..." : "Subscribe"}
        </Button>
      </form>
    </div>
  );
};
